import { ApiError } from '@/lib/api/errors'

import type { LoginInput } from './schema'

/** Figma 2121:51544 — unregistered phone. Used when the API has no business copy (404). */
export const LOGIN_PHONE_UNREGISTERED = 'Nomor ini belum terdaftar.'

/** Figma 2121:51544 — wrong password. Used when the API has no business copy (401/403). */
export const LOGIN_PASSWORD_WRONG = 'Password salah. Periksa kembali password kamu.'

export type LoginErrorField = Extract<keyof LoginInput, 'phone' | 'password'>

/** Credential failure that belongs on a specific login field. */
export class LoginFieldError extends Error {
  readonly field: LoginErrorField

  constructor(field: LoginErrorField, message: string) {
    super(message)
    this.name = 'LoginFieldError'
    this.field = field
  }
}

const UNREGISTERED_HINTS = [
  'tidak terdaftar',
  'belum terdaftar',
  'not registered',
  'not found',
  'tidak ditemukan',
  'user not found',
  'account not found',
] as const

const PASSWORD_HINTS = ['password', 'kata sandi', 'kredensial', 'credential'] as const

function includesAny(haystack: string, needles: readonly string[]) {
  return needles.some((needle) => haystack.includes(needle))
}

/**
 * Map account login failures onto the matching field.
 *
 * 401 / 403 / 404 are HTTP-level failures without useful `message.id`, so we
 * keep the Figma field copy. 400 is a business error — show the API text.
 * Network / 5xx stay generic so the form can show them below the button.
 */
export function mapLoginError(error: unknown): Error {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error : new Error('Login gagal. Coba lagi.')
  }

  if (error.status === 0 || error.status >= 500) return error

  const message = error.message.toLowerCase()

  if (error.status === 404 || includesAny(message, UNREGISTERED_HINTS)) {
    const copy = error.status === 404 ? LOGIN_PHONE_UNREGISTERED : error.message.trim()
    return new LoginFieldError('phone', copy || LOGIN_PHONE_UNREGISTERED)
  }

  if (error.status === 401 || error.status === 403) {
    return new LoginFieldError('password', LOGIN_PASSWORD_WRONG)
  }

  if (includesAny(message, PASSWORD_HINTS)) {
    return new LoginFieldError('password', error.message.trim() || LOGIN_PASSWORD_WRONG)
  }

  if (error.status === 400) {
    return new Error(error.message.trim() || LOGIN_PASSWORD_WRONG)
  }

  return error
}
